import React, { useState, useCallback } from 'react';
import { View, Text, Pressable, FlatList, Alert, TextInput } from 'react-native';
import { MaterialIcons, AntDesign } from '@expo/vector-icons';
import { themeSelector } from '@reduxLocal/selectors/themeSelector';
import { XStack, YStack, SizableText } from 'tamagui';
import { Platform } from 'react-native';
import * as Animatable from 'react-native-animatable';

export interface KnowledgeGroup {
  id: string;
  name: string;
  color?: string;
  icon?: string;
  itemIds: string[];
  category?: string;
  createdAt: string;
}

interface GroupsFlatListProps {
  groups: KnowledgeGroup[];
  onGroupPress: (group: KnowledgeGroup) => void;
  onGroupDelete: (groupId: string) => void;
  onGroupCreate: (groupName: string, category?: string) => void;
  onGroupRename: (groupId: string, newName: string) => void;
  selectedGroupId?: string | null;
  onItemAddToGroup?: (itemId: string, groupId: string) => void;
  onGroupDragOver?: (e: any, groupId: string) => void;
  onGroupDragLeave?: (e: any) => void;
  onGroupDrop?: (e: any, groupId: string) => void;
  dragOverGroupId?: string | null;
}

const GroupsFlatList: React.FC<GroupsFlatListProps> = ({
  groups,
  onGroupPress,
  onGroupDelete,
  onGroupCreate,
  onGroupRename,
  selectedGroupId,
  onItemAddToGroup,
  onGroupDragOver,
  onGroupDragLeave,
  onGroupDrop,
  dragOverGroupId,
}) => {
  const theme = themeSelector();
  const [isCreatingGroup, setIsCreatingGroup] = useState(false);
  const [newGroupName, setNewGroupName] = useState('');
  const [editingGroupId, setEditingGroupId] = useState<string | null>(null);
  const [editGroupName, setEditGroupName] = useState('');

  const handleCreateGroup = useCallback(() => {
    if (!newGroupName.trim()) {
      Alert.alert('Error', 'Please enter a group name');
      return;
    }
    onGroupCreate(newGroupName.trim());
    setNewGroupName('');
    setIsCreatingGroup(false);
  }, [newGroupName, onGroupCreate]);

  const handleStartEdit = useCallback((group: KnowledgeGroup) => {
    setEditingGroupId(group.id);
    setEditGroupName(group.name);
  }, []);

  const handleSaveEdit = useCallback(() => {
    if (!editGroupName.trim() || !editingGroupId) {
      return;
    }
    onGroupRename(editingGroupId, editGroupName.trim());
    setEditingGroupId(null);
    setEditGroupName('');
  }, [editGroupName, editingGroupId, onGroupRename]);

  const handleDeleteGroup = useCallback((group: KnowledgeGroup) => {
    Alert.alert(
      'Delete Group',
      `Are you sure you want to delete "${group.name}"? This will not delete the knowledge items, only remove them from this group.`,
      [
        { text: 'Cancel', style: 'cancel' },
        {
          text: 'Delete',
          style: 'destructive',
          onPress: () => onGroupDelete(group.id),
        },
      ]
    );
  }, [onGroupDelete]);

  const renderGroupItem = useCallback(({ item: group }: { item: KnowledgeGroup }) => {
    const isSelected = selectedGroupId === group.id;
    const isEditing = editingGroupId === group.id;

    return (
      <Animatable.View
        animation="fadeInRight"
        duration={300}
        style={{
          marginRight: 12,
          marginBottom: 12,
        }}
      >
        <Pressable
          onPress={() => !isEditing && onGroupPress(group)}
          onDragOver={Platform.OS === 'web' && onGroupDragOver ? (e) => onGroupDragOver(e, group.id) : undefined}
          onDragLeave={Platform.OS === 'web' && onGroupDragLeave ? onGroupDragLeave : undefined}
          onDrop={Platform.OS === 'web' && onGroupDrop ? (e) => onGroupDrop(e, group.id) : undefined}
          style={{
            backgroundColor: isSelected
              ? theme ? '#3b82f6' : '#2563eb'
              : dragOverGroupId === group.id
              ? theme ? '#dbeafe' : '#1e3a8a'
              : theme ? '#f8fafc' : '#374151',
            borderRadius: 12,
            padding: 12,
            paddingHorizontal: 16,
            minWidth: 120,
            borderWidth: 2,
            borderColor: isSelected
              ? theme ? '#2563eb' : '#1d4ed8'
              : dragOverGroupId === group.id
              ? theme ? '#3b82f6' : '#60a5fa'
              : theme ? '#e5e7eb' : '#4b5563',
            shadowColor: '#000',
            shadowOffset: { width: 0, height: 2 },
            shadowOpacity: 0.1,
            shadowRadius: 4,
            elevation: 3,
            ...(Platform.OS === 'web' && {
              transition: 'all 0.2s ease',
            }),
          }}
        >
          {isEditing ? (
            <XStack alignItems="center" gap="$2">
              <TextInput
                value={editGroupName}
                onChangeText={setEditGroupName}
                style={{
                  flex: 1,
                  color: theme ? '#1f2937' : '#f9fafb',
                  fontSize: 14,
                  fontWeight: '600',
                  padding: 4,
                  backgroundColor: theme ? '#ffffff' : '#1e293b',
                  borderRadius: 6,
                }}
                autoFocus
                onSubmitEditing={handleSaveEdit}
                onBlur={handleSaveEdit}
              />
              <Pressable onPress={handleSaveEdit}>
                <MaterialIcons
                  name="check"
                  size={20}
                  color={theme ? '#10b981' : '#34d399'}
                />
              </Pressable>
            </XStack>
          ) : (
            <YStack gap="$1">
              <XStack alignItems="center" justifyContent="space-between">
                <XStack alignItems="center" gap="$2" flex={1}>
                  {group.icon ? (
                    <MaterialIcons
                      name={group.icon as any}
                      size={18}
                      color={isSelected ? '#ffffff' : (theme ? '#3b82f6' : '#60a5fa')}
                    />
                  ) : (
                    <MaterialIcons
                      name="folder"
                      size={18}
                      color={isSelected ? '#ffffff' : (theme ? '#3b82f6' : '#60a5fa')}
                    />
                  )}
                  <SizableText
                    style={{
                      color: isSelected ? '#ffffff' : (theme ? '#1f2937' : '#f9fafb'),
                      fontSize: 14,
                      fontWeight: '600',
                      flex: 1,
                    }}
                    numberOfLines={1}
                  >
                    {group.name}
                  </SizableText>
                </XStack>
                <XStack gap="$2">
                  <Pressable
                    onPress={(e) => {
                      e.stopPropagation();
                      handleStartEdit(group);
                    }}
                    style={{ padding: 4 }}
                  >
                    <MaterialIcons
                      name="edit"
                      size={16}
                      color={isSelected ? '#ffffff' : (theme ? '#6b7280' : '#9ca3af')}
                    />
                  </Pressable>
                  <Pressable
                    onPress={(e) => {
                      e.stopPropagation();
                      handleDeleteGroup(group);
                    }}
                    style={{ padding: 4 }}
                  >
                    <MaterialIcons
                      name="delete-outline"
                      size={16}
                      color={isSelected ? '#ffffff' : '#ef4444'}
                    />
                  </Pressable>
                </XStack>
              </XStack>
              {group.category && (
                <XStack alignItems="center" gap="$1" marginTop="$1">
                  <MaterialIcons
                    name="label"
                    size={12}
                    color={isSelected ? 'rgba(255,255,255,0.7)' : (theme ? '#9ca3af' : '#6b7280')}
                  />
                  <SizableText
                    style={{
                      color: isSelected ? 'rgba(255,255,255,0.7)' : (theme ? '#9ca3af' : '#6b7280'),
                      fontSize: 11,
                    }}
                  >
                    {group.category}
                  </SizableText>
                </XStack>
              )}
              <SizableText
                style={{
                  color: isSelected ? 'rgba(255,255,255,0.8)' : (theme ? '#6b7280' : '#9ca3af'),
                  fontSize: 12,
                  marginTop: 4,
                }}
              >
                {group.itemIds.length} {group.itemIds.length === 1 ? 'item' : 'items'}
              </SizableText>
            </YStack>
          )}
        </Pressable>
      </Animatable.View>
    );
  }, [theme, selectedGroupId, editingGroupId, editGroupName, handleSaveEdit, handleStartEdit, handleDeleteGroup, onGroupPress]);

  const renderCreateGroupButton = useCallback(() => {
    if (isCreatingGroup) {
      return (
        <Animatable.View
          animation="fadeIn"
          duration={200}
          style={{
            marginRight: 12,
            marginBottom: 12,
            minWidth: 200,
          }}
        >
          <View
            style={{
              backgroundColor: theme ? '#f8fafc' : '#374151',
              borderRadius: 12,
              padding: 12,
              paddingHorizontal: 16,
              borderWidth: 2,
              borderColor: theme ? '#3b82f6' : '#60a5fa',
              borderStyle: 'dashed',
            }}
          >
            <TextInput
              value={newGroupName}
              onChangeText={setNewGroupName}
              placeholder="Enter group name..."
              placeholderTextColor={theme ? '#9ca3af' : '#6b7280'}
              style={{
                color: theme ? '#1f2937' : '#f9fafb',
                fontSize: 14,
                fontWeight: '600',
                padding: 8,
                backgroundColor: theme ? '#ffffff' : '#1e293b',
                borderRadius: 8,
                marginBottom: 8,
              }}
              autoFocus
              onSubmitEditing={handleCreateGroup}
            />
            <XStack gap="$2" justifyContent="flex-end">
              <Pressable
                onPress={() => {
                  setIsCreatingGroup(false);
                  setNewGroupName('');
                }}
                style={{
                  padding: 8,
                  borderRadius: 6,
                  backgroundColor: theme ? '#e5e7eb' : '#4b5563',
                }}
              >
                <Text style={{ color: theme ? '#6b7280' : '#9ca3af', fontSize: 12 }}>
                  Cancel
                </Text>
              </Pressable>
              <Pressable
                onPress={handleCreateGroup}
                style={{
                  padding: 8,
                  borderRadius: 6,
                  backgroundColor: theme ? '#3b82f6' : '#2563eb',
                }}
              >
                <Text style={{ color: '#ffffff', fontSize: 12, fontWeight: '600' }}>
                  Create
                </Text>
              </Pressable>
            </XStack>
          </View>
        </Animatable.View>
      );
    }

    return (
      <Pressable
        onPress={() => setIsCreatingGroup(true)}
        style={{
          marginRight: 12,
          marginBottom: 12,
          backgroundColor: theme ? '#f0f9ff' : '#0c4a6e',
          borderRadius: 12,
          padding: 12,
          paddingHorizontal: 16,
          borderWidth: 2,
          borderColor: theme ? '#3b82f6' : '#60a5fa',
          borderStyle: 'dashed',
          minWidth: 120,
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <XStack alignItems="center" gap="$2">
          <AntDesign
            name="plus"
            size={16}
            color={theme ? '#3b82f6' : '#60a5fa'}
          />
          <Text
            style={{
              color: theme ? '#3b82f6' : '#60a5fa',
              fontSize: 14,
              fontWeight: '600',
            }}
          >
            New Group
          </Text>
        </XStack>
      </Pressable>
    );
  }, [isCreatingGroup, newGroupName, theme, handleCreateGroup]);

  if (groups.length === 0 && !isCreatingGroup) {
    return (
      <View
        style={{
          marginHorizontal: 20,
          marginBottom: 16,
        }}
      >
        <XStack alignItems="center" gap="$2" marginBottom="$2">
          <MaterialIcons
            name="folder"
            size={20}
            color={theme ? '#6b7280' : '#9ca3af'}
          />
          <SizableText
            style={{
              color: theme ? '#374151' : '#d1d5db',
              fontSize: 14,
              fontWeight: '600',
            }}
          >
            Groups
          </SizableText>
        </XStack>
        {renderCreateGroupButton()}
      </View>
    );
  }

  return (
    <View
      style={{
        marginHorizontal: 20,
        marginBottom: 16,
      }}
    >
      <XStack alignItems="center" justifyContent="space-between" marginBottom="$2">
        <XStack alignItems="center" gap="$2">
          <MaterialIcons
            name="folder"
            size={20}
            color={theme ? '#6b7280' : '#9ca3af'}
          />
          <SizableText
            style={{
              color: theme ? '#374151' : '#d1d5db',
              fontSize: 14,
              fontWeight: '600',
            }}
          >
            Groups ({groups.length})
          </SizableText>
        </XStack>
      </XStack>
      <FlatList
        horizontal
        showsVerticalScrollIndicator={false}
        showsHorizontalScrollIndicator={false}
        data={groups}
        keyExtractor={(item) => item.id}
        renderItem={renderGroupItem}
        ListFooterComponent={renderCreateGroupButton}
        contentContainerStyle={{
          paddingRight: 20,
        }}
      />
    </View>
  );
};

export default GroupsFlatList;

